import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getMatches, getPlayers, deleteMatches } from '../services/api';
import type { Match, Player } from '../services/api';
import Matrix from './Matrix';
import { calculateMatricesFromMatches } from '../utils/matrixUtils';
import eventBus from '../utils/eventBus';

interface HistoryPageProps {
  onBack: () => void;
}

const HistoryPage: React.FC<HistoryPageProps> = ({ onBack }) => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<Set<number>>(new Set());
  const [checkedMatchIds, setCheckedMatchIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMatches = useCallback(async () => {
    try {
      setLoading(true);
      const matchesData = await getMatches(false, selectedDate);
      setMatches(matchesData);
      setError(null);
    } catch (_err) {
      console.error("Error fetching matches:", _err);
      setError('Failed to fetch matches for the selected date.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    getPlayers().then(setPlayers).catch(() => setError('Failed to load players.'));
    
    const handleDataChange = () => {
        if (selectedDate) {
            fetchMatches();
        }
    };
    eventBus.on('dataChanged', handleDataChange);

    return () => {
        eventBus.remove('dataChanged', handleDataChange);
    };
  }, [selectedDate, fetchMatches]); // Re-subscribe if selectedDate changes, though not strictly necessary

  useEffect(() => {
    if (selectedDate) {
      fetchMatches();
    }
  }, [selectedDate, fetchMatches]);

  const handlePlayerSelection = (playerId: number) => {
    const newSelectedIds = new Set(selectedPlayerIds);
    if (newSelectedIds.has(playerId)) {
      newSelectedIds.delete(playerId);
    } else {
      newSelectedIds.add(playerId);
    }
    setSelectedPlayerIds(newSelectedIds);
  };

  const handleMatchCheck = (matchId: number) => {
    const newCheckedIds = new Set(checkedMatchIds);
    if (newCheckedIds.has(matchId)) {
      newCheckedIds.delete(matchId);
    } else {
      newCheckedIds.add(matchId);
    }
    setCheckedMatchIds(newCheckedIds);
  };

  const handleSelectAllMatches = (isChecked: boolean) => {
    if (isChecked) {
      setCheckedMatchIds(new Set(filteredMatches.map(m => m.id)));
    } else {
      setCheckedMatchIds(new Set());
    }
  };

  const handleDeleteSelected = async () => {
    if (checkedMatchIds.size === 0) {
      alert('Please select matches to delete.');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${checkedMatchIds.size} selected match(es)?`)) {
      try {
        await deleteMatches(Array.from(checkedMatchIds));
        setCheckedMatchIds(new Set());
        eventBus.dispatch('dataChanged');
      } catch (_err) {
        console.error("Error deleting selected matches:", _err);
        setError('Failed to delete selected matches.');
      }
    }
  };

  const filteredMatches = matches.filter(match => {
    if (selectedPlayerIds.size === 0) {
      return true;
    }
    return match.players.some(player => selectedPlayerIds.has(player.id));
  });

  const { partnerMatrix, opponentMatrix } = useMemo(() => {
    return calculateMatricesFromMatches(filteredMatches, players);
  }, [filteredMatches, players]);

  const playersInFilteredMatches = useMemo(() => {
    const playerMap = new Map<number, Player>();
    const sourceMatches = selectedPlayerIds.size === 0 ? matches : filteredMatches;
    for (const match of sourceMatches) {
      for (const player of match.players) {
        if (!playerMap.has(player.id)) {
          playerMap.set(player.id, player);
        }
      }
    }
    return Array.from(playerMap.values());
  }, [filteredMatches, matches, selectedPlayerIds]);

  return (
    <div>
      <button onClick={onBack} style={{ marginBottom: '1rem' }}>&larr; Back to Dashboard</button>
      <h2>Match History</h2>
      
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem', alignItems: 'flex-start' }}>
        <div>
          <label htmlFor="history-date">Select Date: </label>
          <input 
            type="date" 
            id="history-date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
          />
        </div>
        <div>
          <h4>Filter by Players:</h4>
          <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ccc', padding: '0.5rem' }}>
            {players.map(player => (
              <div key={player.id}>
                <input 
                  type="checkbox"
                  id={`player-${player.id}`}
                  checked={selectedPlayerIds.has(player.id)}
                  onChange={() => handlePlayerSelection(player.id)}
                />
                <label htmlFor={`player-${player.id}`}>{player.name}</label>
              </div>
            ))}
          </div>
          <button onClick={() => setSelectedPlayerIds(new Set(players.map(p => p.id)))}>Select All</button>
          <button onClick={() => setSelectedPlayerIds(new Set())}>Deselect All</button>
        </div>
      </div>

      {checkedMatchIds.size > 0 && (
        <button onClick={handleDeleteSelected} style={{ marginBottom: '1rem', backgroundColor: '#ff4d4f', color: 'white' }}>
          Delete Selected ({checkedMatchIds.size})
        </button>
      )}

      {loading && <p>Loading matches...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      
      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th><input type="checkbox" onChange={e => handleSelectAllMatches(e.target.checked)} /></th>
              <th>Time</th>
              <th>Court</th>
              <th>Team A</th>
              <th>Team B</th>
            </tr>
          </thead>
          <tbody>
            {filteredMatches.map(match => (
              <tr key={match.id}>
                <td><input type="checkbox" checked={checkedMatchIds.has(match.id)} onChange={() => handleMatchCheck(match.id)} /></td>
                <td>{new Date(match.start_time).toLocaleTimeString()}</td>
                <td>{match.court.name}</td>
                <td>{match.players.filter(p => match.team_a.includes(p.id)).map(p => p.name).join(' & ')}</td>
                <td>{match.players.filter(p => match.team_b.includes(p.id)).map(p => p.name).join(' & ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !error && filteredMatches.length > 0 && (
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <Matrix title="Partner Matrix" matrixData={partnerMatrix} players={playersInFilteredMatches} />
            <Matrix title="Opponent Matrix" matrixData={opponentMatrix} players={playersInFilteredMatches} />
        </div>
      )}
    </div>
  );
};

export default HistoryPage;